import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useNavigate, useSearchParams } from 'react-router';
import {
  CampaignComposerPresenter,
  type ComposerCreated,
} from '@/components/campaign-composer/campaign-composer.presenter';
import { DiscardChangesDialog } from '@/components/campaign-composer/discard-changes-dialog';
import { TestDeviceSheet } from '@/components/campaign-composer/test-device-sheet';
import type { PushPreviewPlatform } from '@/components/campaign-composer/push-preview';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/sonner';
import {
  campaignsControllerCreateCampaign,
  campaignsControllerGet,
} from '@/generated/korza-api-client';
import type {
  CampaignDto,
  CreateCampaignDto,
} from '@/generated/korza-api-client/types.gen';
import { useDiscardGuard } from '@/hooks/use-discard-guard';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useSendTestNotification } from '@/hooks/use-send-test-notification';
import { useTestDeviceLinkTicket } from '@/hooks/use-test-device-link-ticket';
import { useTestDeviceStatus } from '@/hooks/use-test-device-status';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import {
  CampaignHeader,
  CampaignLayout,
  CampaignMainContent,
} from '@/layouts/campaign.layout';
import { getApiErrorMessage } from '@/lib/api-error';
import {
  browserStorage,
  clearCampaignDraft,
  loadCampaignDraft,
  saveCampaignDraft,
} from '@/lib/campaign-draft';
import {
  buildCreateCampaignPayload,
  composerReducer,
  createInitialComposerState,
  getBlockingIssues,
  getTestWebsitePath,
} from '@/lib/campaign-composer-form';
import { getPrimaryAction } from '@/lib/campaign-schedule';
import {
  buildTestDeviceDeepLink,
  linkFlowReducer,
  shouldPoll,
} from '@/lib/test-device-link';
import { getLuxonLocale } from '@/lib/utils';

/** Clock of the 2 min link window ("Não recebemos o vínculo…"). */
const LINK_TICK_MS = 1_000;

/** Chips and the main button follow the clock ("Hoje, 19:00" disappears). */
const NOW_TICK_MS = 30_000;

/** Typing pause after which the draft is saved. */
const DRAFT_SAVE_DELAY_MS = 500;

/** "há 5 minutos"; under a minute, "agora há pouco" (not "em 0 segundo"). */
function savedAgo(savedAt: string, locale: Locales, justNow: string): string {
  const saved = DateTime.fromISO(savedAt);
  if (DateTime.now().diff(saved, 'seconds').seconds < 60) return justNow;
  return saved.setLocale(getLuxonLocale(locale)).toRelative() ?? '';
}

type CreateOutcome =
  | { kind: 'created'; campaign: CampaignDto }
  | { kind: 'utmAlreadyUsed' }
  | { kind: 'failed'; message: string };

const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const hasMessageCode = (body: unknown, code: string) =>
  typeof body === 'object' &&
  body !== null &&
  'messageCode' in body &&
  body.messageCode === code;

function useNow(): DateTime {
  const [now, setNow] = useState(() => DateTime.now());
  useEffect(() => {
    const id = setInterval(() => setNow(DateTime.now()), NOW_TICK_MS);
    return () => clearInterval(id);
  }, []);
  return now;
}

/**
 * `/campaigns/create` (PUSH-05, PUSH-06 AC 8, PUSH-03 AC 3): container of
 * the composer. Reads `?date=`, `?audienceId=` and `?from=` (duplicate; a
 * missing campaign opens the empty form with a toast), creates the campaign
 * with the payload built at the click ("Agora" = the next minute of that
 * moment), maps the UTM conflict to Rastreamento and guards unsaved changes.
 */
export default function CreateCampaignPage() {
  const tenant = useTenant();
  const { LL } = useI18nContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const from = searchParams.get('from');
  const [formKey, setFormKey] = useState(0);

  const duplicate = useQuery({
    queryKey: ['campaign-duplicate', tenant.slug, from],
    queryFn: async (): Promise<CampaignDto> => {
      const res = await campaignsControllerGet({
        path: { tenantSlug: tenant.slug, campaignId: from ?? '' },
      });
      if (!res.data || !res.response.ok) throw new Error(String(res.error));
      return res.data;
    },
    enabled: from !== null,
    retry: false,
    staleTime: Infinity,
  });

  const duplicateFailed = duplicate.isError;
  useEffect(() => {
    if (duplicateFailed) toast.error(LL.campaignComposer.duplicateNotFound());
  }, [duplicateFailed, LL]);

  if (from !== null && duplicate.isPending) {
    return (
      <CampaignLayout>
        <CampaignHeader
          title={LL.campaignComposer.header.title()}
          backTo={`/${tenant.slug}/campaigns`}
        />
        <CampaignMainContent className="flex items-center justify-center">
          <Spinner />
        </CampaignMainContent>
      </CampaignLayout>
    );
  }

  return (
    <ComposerForm
      key={formKey}
      duplicateOf={duplicate.data ?? null}
      onCreateAnother={() => {
        setSearchParams({}, { replace: true });
        setFormKey((k) => k + 1);
      }}
    />
  );
}

function ComposerForm({
  duplicateOf,
  onCreateAnother,
}: {
  duplicateOf: CampaignDto | null;
  onCreateAnother(): void;
}) {
  const tenant = useTenant();
  const { LL, locale } = useI18nContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const audiencesEnabled = useFeatureFlags().data?.audiencesEnabled ?? false;
  const now = useNow();

  const [state, dispatch] = useReducer(composerReducer, null, () =>
    createInitialComposerState({
      timezone: browserTimezone(),
      dateParam: searchParams.get('date'),
      audienceIdParam: searchParams.get('audienceId'),
      duplicateOf,
      copySuffix: LL.campaignComposer.copySuffix(),
      storeWebsiteDomain: tenant.storeWebsiteDomain,
    })
  );
  const [previewPlatform, setPreviewPlatform] =
    useState<PushPreviewPlatform>('ios');
  const [success, setSuccess] = useState<ComposerCreated | null>(null);
  const guard = useDiscardGuard(state.dirty && success === null);
  const submitting = useRef(false);

  // Unsent work survives closing the tab: offered back on the next visit
  // (not over a duplicate), saved while editing, gone once created or
  // discarded. Starting to type instead of resuming replaces it.
  const storage = useMemo(browserStorage, []);
  const [offeredDraft, setOfferedDraft] = useState(() =>
    duplicateOf || !storage ? null : loadCampaignDraft(storage, tenant.slug)
  );
  const draftOnOffer = offeredDraft !== null && !state.dirty;
  useEffect(() => {
    if (!storage || !state.dirty || success) return;
    const timeout = setTimeout(
      () => saveCampaignDraft(storage, tenant.slug, state, new Date()),
      DRAFT_SAVE_DELAY_MS
    );
    return () => clearTimeout(timeout);
  }, [storage, state, success, tenant.slug]);
  const clearDraft = () => {
    if (storage) clearCampaignDraft(storage, tenant.slug);
    setOfferedDraft(null);
  };

  // "Testar" (PUSH-08, PUSH-09 AC 2): linked → send right away; not linked →
  // a new one-time ticket per opening and per "Tentar de novo", polling the
  // link status while waiting.
  const [linkFlow, dispatchLink] = useReducer(linkFlowReducer, {
    phase: 'closed',
  });
  const [ticket, setTicket] = useState<string | null>(null);
  const ticketRequest = useRef(0);
  const [checkingDevice, setCheckingDevice] = useState(false);
  const deviceStatus = useTestDeviceStatus({
    enabled: linkFlow.phase !== 'closed',
    poll: shouldPoll(linkFlow),
  });
  const linkTicket = useTestDeviceLinkTicket();
  const sendTest = useSendTestNotification();

  const linked = deviceStatus.data?.linked;
  const statusUpdatedAt = deviceStatus.dataUpdatedAt;
  useEffect(() => {
    if (linked === undefined) return;
    dispatchLink({ type: 'status', linked, now: Date.now() });
  }, [linked, statusUpdatedAt]);

  const waiting = linkFlow.phase === 'waiting';
  useEffect(() => {
    if (!waiting) return;
    const id = setInterval(
      () => dispatchLink({ type: 'tick', now: Date.now() }),
      LINK_TICK_MS
    );
    return () => clearInterval(id);
  }, [waiting]);

  const requestTicket = () => {
    const request = ++ticketRequest.current;
    setTicket(null);
    linkTicket.mutate(undefined, {
      onSuccess: (issued) => {
        if (request === ticketRequest.current) setTicket(issued.ticket);
      },
    });
  };

  const closeLinkSheet = () => {
    ticketRequest.current += 1;
    setTicket(null);
    dispatchLink({ type: 'close' });
  };

  const sendTestNotification = (onSent?: () => void) =>
    sendTest.mutate(
      {
        title: state.title,
        body: state.body,
        websitePath: getTestWebsitePath(state.link, tenant.storeWebsiteDomain),
      },
      {
        onSuccess: () => {
          toast.success(LL.campaignComposer.testDevice.sent());
          onSent?.();
        },
        onError: (error) => toast.error(error.message),
      }
    );

  const test = async () => {
    setCheckingDevice(true);
    const result = await deviceStatus.refetch();
    setCheckingDevice(false);
    if (result.isError) {
      toast.error(LL.campaignComposer.testDevice.statusError());
      return;
    }
    if (result.data?.linked) {
      sendTestNotification();
      return;
    }
    dispatchLink({ type: 'open', now: Date.now() });
    requestTicket();
  };

  const create = useMutation({
    mutationFn: async (body: CreateCampaignDto): Promise<CreateOutcome> => {
      const res = await campaignsControllerCreateCampaign({
        path: { tenantSlug: tenant.slug },
        body,
      });
      if (res.data && res.response.ok) {
        return { kind: 'created', campaign: res.data };
      }
      if (
        res.response.status === 400 &&
        hasMessageCode(res.error, 'utmCampaignValueAlreadyUsed')
      ) {
        return { kind: 'utmAlreadyUsed' };
      }
      return {
        kind: 'failed',
        message: getApiErrorMessage(
          res.error,
          LL.campaignComposer.createError()
        ),
      };
    },
  });

  const ctx = { storeWebsiteDomain: tenant.storeWebsiteDomain, now };
  const blockingIssues = getBlockingIssues(state, ctx);
  const primaryAction = getPrimaryAction(
    state.schedule,
    now,
    state.timezone,
    locale
  );

  const submit = () => {
    if (submitting.current) return;
    const clickedAt = DateTime.now();
    const payload = buildCreateCampaignPayload(state, {
      now: clickedAt,
      storeWebsiteDomain: tenant.storeWebsiteDomain,
      audiencesEnabled,
    });
    if (!payload) return;

    const action = getPrimaryAction(
      state.schedule,
      clickedAt,
      state.timezone,
      locale
    );
    const whenLabel =
      action.kind !== 'schedule'
        ? ''
        : action.day === 'today'
          ? LL.campaignComposer.success.scheduledToday({ time: action.time })
          : action.day === 'tomorrow'
            ? LL.campaignComposer.success.scheduledTomorrow({
                time: action.time,
              })
            : LL.campaignComposer.success.scheduledDate({
                date: action.dateLabel,
                time: action.time,
              });

    submitting.current = true;
    create.mutate(payload, {
      onSuccess: (outcome) => {
        if (outcome.kind === 'created') {
          void queryClient.invalidateQueries({
            queryKey: ['campaigns', tenant.slug],
          });
          clearDraft();
          setSuccess({
            kind: action.kind === 'sendNow' ? 'sentNow' : 'scheduled',
            whenLabel,
            campaignId: outcome.campaign.id,
            at: DateTime.fromISO(payload.launchDateTime, {
              zone: state.timezone,
            }),
          });
        } else if (outcome.kind === 'utmAlreadyUsed') {
          dispatch({ type: 'utmAlreadyUsed' });
        } else {
          toast.error(outcome.message);
        }
      },
      onError: (error) => {
        toast.error(
          getApiErrorMessage(error, LL.campaignComposer.createError())
        );
      },
      onSettled: () => {
        submitting.current = false;
      },
    });
  };

  const linkFlowOpen = linkFlow.phase !== 'closed';

  return (
    <>
      <CampaignComposerPresenter
        state={state}
        dispatch={dispatch}
        blockingIssues={blockingIssues}
        primaryAction={primaryAction}
        now={now}
        tenant={{
          slug: tenant.slug,
          iconUrl: tenant.iconUrl,
          name: tenant.name,
          storeWebsiteDomain: tenant.storeWebsiteDomain,
        }}
        audiencesEnabled={audiencesEnabled}
        isSubmitting={create.isPending}
        onSubmit={submit}
        onCancel={() => navigate(`/${tenant.slug}/campaigns`)}
        onTest={() => void test()}
        isTesting={checkingDevice || (sendTest.isPending && !linkFlowOpen)}
        previewPlatform={previewPlatform}
        onPreviewPlatformChange={setPreviewPlatform}
        created={success}
        onCreateAnother={onCreateAnother}
        draft={
          draftOnOffer
            ? {
                savedLabel: savedAgo(
                  offeredDraft.savedAt,
                  locale,
                  LL.campaignComposer.draft.justNow()
                ),
              }
            : null
        }
        onResumeDraft={() => {
          if (offeredDraft)
            dispatch({ type: 'reset', state: offeredDraft.state });
          setOfferedDraft(null);
        }}
        onDiscardDraft={clearDraft}
      />
      <TestDeviceSheet
        open={linkFlowOpen}
        onOpenChange={(open) => {
          if (!open) closeLinkSheet();
        }}
        phase={linkFlow.phase}
        deepLink={
          ticket ? buildTestDeviceDeepLink(tenant.appScheme, ticket) : null
        }
        ticketError={linkTicket.isError}
        onRetry={() => {
          dispatchLink({ type: 'retry', now: Date.now() });
          requestTicket();
        }}
        onSend={() => sendTestNotification(closeLinkSheet)}
        isSending={sendTest.isPending}
        appStoreUrl={tenant.appStoreAppUrl}
        playStoreUrl={tenant.googlePlayAppUrl}
        onOpenApp={() => guard.bypass(() => undefined)}
      />
      <DiscardChangesDialog
        open={guard.blocker.state === 'blocked'}
        onDiscard={() => {
          clearDraft();
          guard.proceed();
        }}
        onKeepEditing={guard.reset}
      />
    </>
  );
}
